import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { addAttributeRuntimeOption } from "@/lib/catalog/attributeOptionsApi";
import type { Attribute, AttributeOption } from "@/lib/catalog/types";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  attribute: Attribute;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (options: AttributeOption[], selectedValue: string) => void;
};

export function AttributeRuntimeOptionDialog({ attribute, open, onOpenChange, onSuccess }: Props) {
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setLabel("");
  }, [open, attribute.id]);

  const save = async () => {
    if (saving) return;
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("Option label is required.");
      return;
    }
    setSaving(true);
    try {
      const result = await addAttributeRuntimeOption(attribute.id, trimmed);
      onSuccess(result.attribute.options ?? [], result.optionValue);
      onOpenChange(false);
      toast.success("Option added.");
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "response" in error
          ? ((error as { response?: { data?: { message?: string } } }).response?.data?.message ??
            "Unable to add option.")
          : "Unable to add option.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add {attribute.name} option</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-xs text-muted-foreground">Label</Label>
          <Input
            className="glass-input h-10"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void save();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="premium" onClick={() => void save()} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
